// 既存 RenderQuestionnaire.vue:onSubmit L1790-2200 のフィールド処理を関数化
// 旧版と byte レベルで同一の payload を生成することが目標 (Q4CL-console との互換性必須)
//
// 使い方:
//   const result = await window.fieldSerializer.process({
//     fields: this.fields,             // 元の問診票 fields (this.fields)
//     now: new Date(),                  // 送信時刻
//     encryptParams: enc.encryptParams, // info.encryption に入れる
//     i18nAs: (s) => translatorPromise, // 翻訳関数 (国際化メッセージ用) — オプション
//   })
//   if (result.arrEmptyRequired.length) ... // 必須エラー
//   if (result.validationErrors.length) ... // バリデーションエラー
//   await firebase.database().ref(`questionnaire/answers/${userId}/${esc}/${answerId}`).set(result.payload)
//   await firebase.database().ref(`questionnaire/answersTextOnly/${userId}/${esc}/${answerId}`).set(result.payloadTextOnly)
//   await firebase.database().ref(`questionnaire/answerlist/${userId}/${esc}/${answerId}`).set(result.answerlistData)
;(function () {
  // ============================================================
  // setHiddenProps — parentname/parentsel による条件付き表示
  // 旧 RenderQuestionnaire.vue:1720-1747 と同一
  // ============================================================
  function setHiddenProps(fields) {
    const esc = window.mixinUtils.escapeFbRtdbKey;
    for (const key in fields) {
      const f = fields[key];
      if (f.parentname && f.parentsel) {
        const fp = fields[f.parentname];
        if (!fp) continue;
        const parentSels = Array.isArray(f.parentsel) ? f.parentsel : String(f.parentsel).split('|');
        let checked = false;
        for (const sel of parentSels) {
          if (fp.sels && fp.sels[esc(sel)]) {
            checked = checked || fp.sels[esc(sel)].checked;
          }
        }
        f.hidden = !!fp.hidden || !checked;
      }
    }
    return fields;
  }

  // ============================================================
  // メイン処理 — 旧 onSubmit (L1790-2162) と同一ロジック
  // ============================================================
  async function process(opts) {
    const _ = window._; // lodash
    const enc = window.encryption;
    const Q4CL = window.Q4CL;
    const escapeFbRtdbKey = window.mixinUtils.escapeFbRtdbKey;
    const i18nAs = opts.i18nAs || (async (s) => s);
    const presentDialog = window.mixinUtils.presentDialog;

    const fields = {};
    const arrEmptyRequired = [];
    const validationErrors = [];
    const clonedFields = _.cloneDeep(opts.fields);
    const fieldsForEval = _.cloneDeep(clonedFields);
    const evalResults = {};

    // ----- checkbox の sels.checked を value (パイプ区切り文字列) に展開 -----
    for (const key in clonedFields) {
      const fld = clonedFields[key];
      if (fld.type === 'checkbox') {
        const arrChecked = [];
        const arrCheckedIdx = [];
        if (fld.sels) {
          for (const keySub in fld.sels) {
            if (fld.sels[keySub].checked) {
              arrChecked.push(keySub);
              arrCheckedIdx.push(fld.sels[keySub].index);
            }
          }
        }
        if (arrChecked.length > 0) {
          clonedFields[key].value = arrChecked.join('|');
          fieldsForEval[key].value = arrChecked;
        }
        if (arrCheckedIdx.length > 0) {
          fieldsForEval[key + '.index'] = {
            name: key + '.index',
            type: 'index',
            value: arrCheckedIdx
          };
        }
      }
    }

    const privacyItemNames = Q4CL.privacyItems.map((x) => x.name);

    // ★ eval 式から参照可能なヘルパー関数 (旧版 RenderQuestionnaire.vue:416-450 の scope 移植)
    function momentSince(unit, from, to) {
      if (!from) return;
      try { return (to ? moment(String(to), 'YYYY-MM-DD') : moment()).diff(moment(String(from), 'YYYY-MM-DD'), unit); } catch (e) { return; }
    }
    const yearsSince = (from, to) => momentSince('years', from, to);
    const monthsSince = (from, to) => momentSince('months', from, to);
    const weeksSince = (from, to) => momentSince('weeks', from, to);
    const daysSince = (from, to) => momentSince('days', from, to);
    const average = (...args) => {
      let sum = 0.0, count = 0;
      args.forEach((v) => { if (v !== '' && !isNaN(v)) { sum += Number(v); count++; } });
      return count > 0 ? sum / count : NaN;
    };
    const sum = (...args) => {
      let s = NaN;
      args.forEach((v) => { if (v !== '' && !isNaN(v)) { s = isNaN(s) ? Number(v) : s + Number(v); } });
      return s;
    };

    // ----- 各フィールドを type 別に処理 -----
    for (const key in clonedFields) {
      const field = _.cloneDeep(clonedFields[key]);

      // 値を持たない型はスキップ
      if (['embed', 'separator', 'note', 'clipart'].includes(field.type)) continue;

      let formulaForEval = '';

      if (typeof field.hidden === 'undefined' || !field.hidden) {
        // ===== 必須入力チェック =====
        if (field.required) {
          let bAllUnchecked = true;
          switch (field.type) {
            case 'radio':
            case 'checkbox':
              for (const k in field.sels) {
                if (field.sels[k].checked) bAllUnchecked = false;
              }
              if (bAllUnchecked) arrEmptyRequired.push(field.name);
              break;
            case 'text':
            case 'date':
            case 'time':
              if (!field.value || String(field.value).trim() === '') arrEmptyRequired.push(field.name);
              break;
            case 'textarea':
              if (!field.text || String(field.text).trim() === '') arrEmptyRequired.push(field.name);
              break;
            case 'scale':
              if (field.value === null || field.value === '' || field.value < field.min || field.value > field.max) {
                arrEmptyRequired.push(field.name);
              }
              break;
            case 'signpad':
              if (!field.encryptedImgData && !field.src) arrEmptyRequired.push(field.name);
              break;
          }
        }

        if (arrEmptyRequired.length === 0) {
          // ===== type 別の値整形・暗号化 =====
          switch (field.type) {
            case 'radio': {
              const fval = escapeFbRtdbKey(field.value);
              if (field.sels && field.sels[fval] && field.sels[fval].index) {
                field.index = field.sels[fval].index;
                fieldsForEval[key + '.index'] = {
                  name: key + '.index',
                  type: 'index',
                  value: field.index
                };
              }
              break;
            }

            case 'text': {
              const han2zen = (str) =>
                str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));

              if (field.name === 'ptPhone' && field.value) {
                field.value = han2zen(field.value.trim());
                if (!/^[0+][\d-]+$/.test(field.value)) validationErrors.push('電話番号');
              }
              if (field.name === 'ptEmail' && field.value) {
                field.value = han2zen(field.value.trim());
                if (!/^[\w\-._+]+@[\w\-._]+\.[A-Za-z]+$/.test(field.value)) validationErrors.push('メールアドレス');
              }

              const textToEncrypt = privacyItemNames.includes(field.name)
                ? field.value
                : await window.i18nStore.toJapanese(field.value);
              field.value = await enc.encryptStr(textToEncrypt);
              break;
            }

            case 'textarea': {
              const textToEncrypt = privacyItemNames.includes(field.name)
                ? field.text
                : await window.i18nStore.toJapanese(field.text);
              field.text = await enc.encryptStr(textToEncrypt);
              break;
            }

            case 'getphoto':
              if (Array.isArray(field.photos)) {
                field.photos.forEach((photo, idx) => {
                  field.photos[idx].image.dataUrl = field.photos[idx].image.encryptedImgData;
                  delete field.photos[idx].image.encryptedImgData;
                  delete field.photos[idx].image.blobUrl;
                });
              }
              break;

            case 'figure':
            case 'signpad':
              field.src = field.encryptedImgData || field.src || '';
              delete field.baseImgSrc;
              delete field.encryptedImgData;
              break;

            case 'date':
              if (field.value && typeof field.value !== 'undefined') {
                field.value = await enc.encryptStr(moment(field.value).format(field.format));
              }
              break;

            case 'time':
              if (field.value && typeof field.value !== 'undefined') {
                field.value = moment(field.value).format(field.format);
              }
              break;

            case 'scale':
              if (field.subtype === 'bodytemp') {
                field.value = field.value == 0 ? '' : String((field.value / 10).toFixed(1)) + '℃';
              } else if (field.subtype === 'bloodpressure') {
                field.value =
                  field.value.lower == 0 && field.value.upper == 0
                    ? ''
                    : String(field.value.upper) + '/' + String(field.value.lower) + 'mmHg';
              } else {
                field.value =
                  field.value >= field.min && field.max >= field.value
                    ? field.value + (field.unit ? String(field.unit) : 0.0)
                    : '';
              }
              break;

            case 'eval':
            case 'emergency':
            case 'warning': {
              // ★ eval 機能 — 旧版と一字一句同じ実装 (バグも含めて移植)
              formulaForEval = fieldsForEval[key].formula
                .replace(/%%(.+?)%%/g, " (fieldsForEval['$1']? fieldsForEval['$1'].value: '') ")
                .replace(/{{(.+?)}}/g, " String(fieldsForEval['$1']? fieldsForEval['$1'].value: '') ")
                .replace(/\[\[(.+?)\]\]/g, " parseInt(fieldsForEval['$1']? fieldsForEval['$1'].value: 0) ")
                .replace(/\(\((.+?)\)\)/g, " parseFloat(fieldsForEval['$1']? fieldsForEval['$1'].value: 0) ")
                .replace(/(;|let |const |var |eval\s*\(|=>|{\s*})/g, '');

              if (formulaForEval !== fieldsForEval[key].formula) {
                try {
                  // 旧版と同じ生 eval を実行 (fieldsForEval をスコープに保持)
                  field.value = eval(formulaForEval);

                  // debugOpts.evalLog でゲート (デフォルト on、 トグル/URL 引数で off)
                  if (window.debugOpts?.evalLog && document.location.hostname === 'localhost') {
                    console.log('[eval]', key, 'formula:', formulaForEval, '=>', field.value);
                  }

                  if (Number.isNaN(field.value)) field.value = null;
                  fieldsForEval[key].value = field.value;
                } catch (error) {
                  console.error('[eval error] key=' + key + ' formula=' + JSON.stringify(formulaForEval) + ' err=' + String(error));
                  // emergency/warning はエラー時に発報してはいけない (falsy 必須)
                  field.value = (field.type === 'emergency' || field.type === 'warning') ? '' : 'NA';
                }
              } else {
                field.value = '';
              }

              if (field.type === 'emergency' || field.type === 'warning') {
                evalResults[field.type] = !!field.value;
              }
              break;
            }
          }

          fields[escapeFbRtdbKey(key)] = _.cloneDeep(field);
        }
      }
    }

    return { fields, arrEmptyRequired, validationErrors, evalResults, clonedFields };
  }

  // ============================================================
  // build payloads — process の結果 + 送信メタ情報から 3 経路の body を構築
  // ============================================================
  function buildPayloads(opts) {
    const _ = window._;
    const Q4CL = window.Q4CL;
    const { fields, evalResults, encryptParams, now } = opts;

    // info ブロックを追加
    fields.info = {
      uploadtime: moment(now).format('YYYY-MM-DD HH:mm:ss'),
      encryption: JSON.stringify(encryptParams)
    };
    if (evalResults.emergency || evalResults.warning) {
      fields.info.alert = evalResults.emergency ? 'emergency' : 'warning';
    }

    // privacyItems を info にも重複保存 (暗号化済みの値)
    _.map(Q4CL.privacyItems, 'name').forEach((item) => {
      if (fields[item] && fields[item].value) {
        fields.info[item] = fields[item].value;
      }
    });

    // テキストデータのみ (画像系を除外)
    const fieldsTextOnly = {};
    for (const fieldName in fields) {
      if (!['figure', 'signpad', 'getphoto'].includes(fields[fieldName].type)) {
        fieldsTextOnly[fieldName] = fields[fieldName];
      }
    }

    return { fields, fieldsTextOnly };
  }

  // ============================================================
  // 施設向け通知メールのパラメータ。 宛先・本文は api/q4cl_notify.php が RTDB から組み立てる
  // ============================================================
  function buildMailNotificationParams(userId, qName, answerId) {
    const params = new URLSearchParams();
    params.append('uid', userId);
    params.append('qname', qName);
    params.append('answerid', answerId);
    return params;
  }

  /**
   * 旧 Q4CL-client/components/RenderQuestionnaire.vue:1646-1694 を完全踏襲。
   * field が「必須かつ空」 かを動的判定 (送信前から常に評価される)。
   * 結果を <div class="required-empty"> 等のクラスバインドに使うことで、
   * ユーザーが値を入れた瞬間に false → クラス解除 → ピンク背景解除 となる。
   */
  function isRequiredAndEmpty(field) {
    if (!field || !field.required) return false;

    switch (field.type) {
      case 'text':
      case 'date':
      case 'time':
        return !field.value || String(field.value).trim() === '';

      case 'textarea':
        return !field.text || String(field.text).trim() === '';

      case 'radio':
        return !field.value || field.value === '';

      case 'checkbox':
        if (!field.sels) return true;
        return !Object.values(field.sels).some((sel) => sel.checked);

      case 'scale':
      case 'painscale':
      case 'bodytemp':
      case 'bloodpressure':
        if (field.subtype === 'bloodpressure') {
          return (
            !field.value ||
            (field.value.lower === 0 && field.value.upper === 0)
          );
        }
        // 旧版バグ修正: painscale は「痛みなし=0」 が正常入力なので 0 を未入力扱いしない。
        // 初期値 -1 を 「未選択」 のセンチネルとして使い、 < 0 のみ未入力と判定。
        // (initializeField で painscale は field.value = -1 を必ず設定する)
        if (field.subtype === 'painscale') {
          return (
            field.value === null ||
            field.value === '' ||
            typeof field.value !== 'number' ||
            field.value < 0
          );
        }
        return (
          field.value === null ||
          field.value === '' ||
          field.value === -1 ||
          field.value === 0
        );

      case 'signpad':
        return !field.src && !field.encryptedImgData;

      case 'figure':
        return !field.src && !field.encryptedImgData;

      case 'getphoto':
        return !field.photos || field.photos.length === 0;

      default:
        return false;
    }
  }

  window.fieldSerializer = {
    setHiddenProps,
    isRequiredAndEmpty,
    process,
    buildPayloads,
    buildMailNotificationParams,
    NOTIFY_API_URL: 'https://api.emuyn.net/q4cl_notify.php'
  };
})();
